/**
 * 缓存管理工具
 * 用于处理应用更新时的缓存清理和版本检测
 */

interface BuildInfo {
  buildId: string
  buildTime: string
  gitCommit: string
  version: string
}

class CacheManager {
  private static instance: CacheManager
  private currentBuildId: string | null = null
  private buildInfo: BuildInfo | null = null
  private translations: Record<string, unknown> = {}

  private constructor() {
    this.initializeBuildInfo()
    // 只在客户端加载翻译
    if (typeof window !== 'undefined') {
      this.loadTranslations()
    }
  }

  static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager()
    }
    return CacheManager.instance
  }

  /**
   * 加载国际化翻译
   */
  private async loadTranslations() {
    // 确保在客户端环境
    if (typeof window === 'undefined') {
      return
    }
    
    try {
      // 获取当前语言
      const currentLanguage = localStorage.getItem('language') || 'zh-CN'
      
      // 动态加载翻译文件
      const response = await fetch(`/locales/${currentLanguage}/common.json`)
      if (response.ok) {
        this.translations = await response.json()
      }
    } catch (error) {
      console.error('Failed to load translations:', error)
      // 使用默认翻译
      this.translations = {
        update: {
          title: '应用已更新',
          message: '发现新版本，缓存已自动清理',
          refresh: '立即刷新页面'
        }
      }
    }
  }

  /**
   * 获取翻译文本
   */
  private t(key: string, defaultValue: string): string {
    const keys = key.split('.')
    let value: unknown = this.translations
    
    for (const k of keys) {
      value = (value as Record<string, unknown>)?.[k]
    }
    
    return (typeof value === 'string' ? value : defaultValue)
  }

  /**
   * 初始化构建信息
   */
  private initializeBuildInfo() {
    if (typeof window !== 'undefined') {
      this.buildInfo = {
        buildId: process.env.NEXT_PUBLIC_BUILD_ID || 'unknown',
        buildTime: process.env.NEXT_PUBLIC_BUILD_TIME || '',
        gitCommit: process.env.NEXT_PUBLIC_GIT_COMMIT || '',
        version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'
      }
      
      this.currentBuildId = this.buildInfo.buildId
      
      // 存储当前构建ID到 localStorage
      const storedBuildId = localStorage.getItem('app_build_id')
      if (storedBuildId && storedBuildId !== this.currentBuildId) {
        console.log('🔄 检测到新版本，准备清理缓存...')
        this.handleVersionUpdate(storedBuildId, this.currentBuildId)
      }
      
      localStorage.setItem('app_build_id', this.currentBuildId)
    }
  }

  /**
   * 处理版本更新
   */
  private handleVersionUpdate(oldBuildId: string, newBuildId: string) {
    console.log(`📦 版本更新: ${oldBuildId} → ${newBuildId}`)
    
    // 清理应用缓存
    this.clearApplicationCache()
    
    // 显示更新通知
    this.showUpdateNotification()
  }

  /**
   * 清理应用缓存
   */
  private clearApplicationCache() {
    try {
      // 1. 清理 localStorage（保留重要用户数据）
      const preserveKeys = ['language', 'theme', 'user_preferences']
      const keysToRemove: string[] = []
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && !preserveKeys.includes(key) && key !== 'app_build_id') {
          keysToRemove.push(key)
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key))
      
      // 2. 清理 sessionStorage
      sessionStorage.clear()
      
      // 3. 清理 Service Worker 缓存（如果有）
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister()
          })
        })
      }
      
      // 4. 清理浏览器缓存
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name)
          })
        })
      }
      
      console.log('🧹 应用缓存已清理')
    } catch (error) {
      console.error('❌ 清理缓存时出错:', error)
    }
  }

  /**
   * 显示更新通知
   */
  private async showUpdateNotification() {
    // 确保翻译已加载
    if (!this.translations.update) {
      await this.loadTranslations()
    }
    
    // 获取带版本号的URL
    const getVersionedUrl = () => {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set('v', this.currentBuildId || 'latest')
      return currentUrl.toString()
    }
    
    // 获取翻译文本
    const title = this.t('update.title', '应用已更新')
    const message = this.t('update.message', '发现新版本，缓存已自动清理')
    const refreshText = this.t('update.refresh', '立即刷新页面')
    
    // 创建一个非阻塞的更新通知
    const notification = document.createElement('div')
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 16px 20px;
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        max-width: 320px;
        animation: slideInRight 0.3s ease-out;
      ">
        <div style="display: flex; align-items: center; margin-bottom: 8px;">
          <svg width="20" height="20" style="margin-right: 8px;" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <strong>${title}</strong>
        </div>
        <div style="margin-bottom: 12px; opacity: 0.9;">
          ${message}
        </div>
        <button onclick="window.location.href='${getVersionedUrl()}'" style="
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
        " onmouseover="this.style.background='rgba(255,255,255,0.3)'" onmouseout="this.style.background='rgba(255,255,255,0.2)'">
          ${refreshText}
        </button>
      </div>
      <style>
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `
    
    document.body.appendChild(notification)
    
    // 10秒后自动移除通知
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, 10000)
  }

  /**
   * 手动检查更新
   */
  async checkForUpdates(): Promise<boolean> {
    try {
      const response = await fetch('/api/build-info', { 
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      })
      
      if (response.ok) {
        const serverBuildInfo: BuildInfo = await response.json()
        
        if (serverBuildInfo.buildId !== this.currentBuildId) {
          this.handleVersionUpdate(this.currentBuildId || 'unknown', serverBuildInfo.buildId)
          return true
        }
      }
      
      return false
    } catch (error) {
      console.error('检查更新失败:', error)
      return false
    }
  }

  /**
   * 获取当前构建信息
   */
  getBuildInfo(): BuildInfo | null {
    return this.buildInfo
  }

  /**
   * 强制刷新页面并清理缓存
   */
  forceRefresh() {
    this.clearApplicationCache()
    
    // 使用带版本号的URL刷新
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.set('v', this.currentBuildId || 'latest')
    window.location.href = currentUrl.toString()
  }
}

export default CacheManager.getInstance()